/**
 * `AuftragGespraechKarte` — Kartenrahmen des Gespraechs auf der Vorgangsseite. Seit dem Mail-Fenster
 * (docs/KONZEPT-GESPRAECH-MAILFENSTER.md, Mockup V4 freigegeben 21.10.2026) stehen die Nachrichten
 * nicht mehr als Chat-Blasen da, sondern in `GespraechMailfenster`: drei Reiter Gast/Betrieb/Verlauf,
 * Liste wie Apple Mail (neueste oben), geoeffnete Nachricht, Antwort-Fenster unten.
 *
 * Diese Karte behaelt, was um die Nachrichten herum gehoert: Quelle-Badge, `HerkunftPille` (Kanal +
 * „Testanfrage“), Sprache-Chip, „Als Testanfrage kennzeichnen“, „Zur Anfrage →“, Lieferadresse, den
 * Original/Deutsch-Umschalter + „Jetzt übersetzen“ (E2E-Golden-Path
 * `auftrags-dashboard-anfrage-uebersetzung.spec.ts`; wirkt auf die SYNTHETISCHE Gast-Nachricht, nur
 * relevant, wenn `quelle !== "email"`) und „E-Mail zuordnen“ (`NachrichtAuswaehlenDrawer`).
 *
 * Datenquelle: alle Props kommen von `pages/event-detail.tsx`, das `emails`/`activity`/`offer-history`
 * ohnehin fuer `AuftragVerlaufKarte` laedt. Die Einteilung gast/betrieb und das Zusammenfassen von
 * Betriebshinweisen an mehrere Adressen macht der SERVER (`GET /api/events/:id/emails`, Felder `art`
 * und `empfaenger`). Sortierung nach ECHTER Sendezeit (`lib/gespraech.ts::sendezeitpunkt`).
 *
 * Ohne einzige E-Mail, aber mit Anfragetext (Formular/manuell erfasst) entsteht eine SYNTHETISCHE
 * erste Gast-Nachricht. Ein Gespraech ohne Nachrichten bleibt sinnvoll (Antwort-Fenster bedienbar),
 * darum immer ein Leerzustand statt Verschwinden.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Icon, Card } from "../components";
import { apiMutate } from "../../hooks/useApi";
import { useCompanyName } from "../../hooks/useCompanyName";
import type { ApiError } from "../../lib/api";
import { LANG_LABEL } from "../translation-review";
import { HerkunftPille } from "../HerkunftPille";
import type { Herkunft } from "@maestro/auswertung";
import { sendezeitpunkt, sortiereNachSendezeit } from "../../lib/gespraech";
import type { AuftragVerlaufEintrag } from "../../lib/auftrag-verlauf";
import { NachrichtAuswaehlenDrawer } from "../NachrichtAuswaehlenDrawer";
import { GespraechMailfenster } from "./GespraechMailfenster";
import { SYNTHETISCHE_NACHRICHT_ID, type GespraechEmailRow, type InquiryTranslation } from "./gespraech-typen";

export type { GespraechEmailRow, InquiryTranslation } from "./gespraech-typen";

const CARD_TITLE = "font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface";

const SERVICE_KIND_LABEL: Record<string, string> = {
  catering: "Catering (außer Haus)",
  event: "Im Haus",
};

function formatiereDatum(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export interface AuftragGespraechKarteProps {
  /** `inquiries.id` der erzeugenden Anfrage — ohne sie (z. B. Shop-Handoff-Vorgänge ohne Anfrage-
   *  Umweg) zeigt die Karte weder den Link "Zur Anfrage" noch das Test-Kennzeichen. */
  inquiryId: string | null;
  /** `events.customer_notes` — nur noch Grundlage der SYNTHETISCHEN ersten Gast-Blase, wenn es
   *  überhaupt keine E-Mails gibt (Formular-Anfrage, telefonisch erfasst). */
  customerNotes: string | null;
  originChannel: string | null;
  serviceKind: "catering" | "event" | null;
  deliveryAddress: string | null;
  inquiryLanguage: string | null;
  inquiryTranslation: InquiryTranslation | null;
  herkunft?: Herkunft | null;
  inquiryIsTest?: boolean;
  /** Nach dem Umschalten des Test-Kennzeichens — Elternkomponente lädt neu. */
  onChanged?: () => void;
  /** Nach einem erfolgreichen manuellen "Jetzt übersetzen" — Elternkomponente lädt das Event neu
   *  (`inquiryTranslation`/`inquiryLanguage` kommen von dort). */
  onTranslated?: () => void;
  eventId: string;
  /** Volle Mail-Zeilen — dieselben, die `AuftragVerlaufKarte` schon bekommt (kein zweiter Fetch). */
  emails: GespraechEmailRow[];
  emailsLoading: boolean;
  /** Dieselbe abgeleitete Liste wie `AuftragVerlaufKarte` (`leiteAuftragsVerlaufAb` +
   *  Eckdaten-Sammelzeilen) — hier gefiltert auf alles AUSSER `mail`/`version` (die erscheinen
   *  bereits als Nachricht-Blase). */
  verlaufEintraege: AuftragVerlaufEintrag[];
  /** Ansprechpartner-Name/Firma des Kunden — Sender-Label der Gast-Blasen und "Antwort an …". */
  kundenName: string | null;
  kundenFirma: string | null;
  /** E-Mail des Kunden — „An“-Zeile des Antwort-Fensters (ohne Angabe: Absender der Gast-Nachricht). */
  kundenEmail?: string | null;
  /** „Silvester Gala-Dinner · 31.12.2026 · V-1252“ — Unterzeile unter dem Betreff der geoeffneten Nachricht. */
  vorgangInfo?: string | null;
  /** Nach erfolgreichem Senden — Elternkomponente lädt `emailsQ`/`activityQ` neu. */
  onGesendet?: () => void;
  /** Nach einer erfolgreichen Zuordnung (zuordnen/ändern/aufheben) — Elternkomponente lädt
   *  `emailsQ` neu (Chip/„⋯"-Menü hängen an derselben `emails`-Liste). */
  onZuordnungGeaendert?: () => void;
}

export function AuftragGespraechKarte({
  inquiryId,
  customerNotes,
  originChannel,
  serviceKind,
  deliveryAddress,
  inquiryLanguage,
  inquiryTranslation,
  herkunft,
  inquiryIsTest,
  onChanged,
  onTranslated,
  eventId,
  emails,
  emailsLoading,
  verlaufEintraege,
  kundenName,
  kundenFirma,
  kundenEmail,
  vorgangInfo,
  onGesendet,
  onZuordnungGeaendert,
}: AuftragGespraechKarteProps) {
  const [fehler, setFehler] = useState<string | null>(null);
  const [zuordnenOffen, setZuordnenOffen] = useState(false);
  const [ansicht, setAnsicht] = useState<"original" | "de">("original");
  const [uebersetzeLaeuft, setUebersetzeLaeuft] = useState(false);
  const [uebersetzeFehler, setUebersetzeFehler] = useState<string | null>(null);
  const firmaName = useCompanyName();

  const notizText = (customerNotes ?? "").trim();
  const istFormular = originChannel === "form" && notizText.length > 0;

  const sortierteEmails = useMemo(() => sortiereNachSendezeit(emails), [emails]);
  const ersteEingehende = useMemo(() => sortierteEmails.find((m) => m.direction === "inbound") ?? null, [sortierteEmails]);

  // E8 (unverändert aus der alten Anfrage-Karte): Quelle-Badge-Label je nachdem, WOMIT dieser
  // Vorgang angefangen hat — unabhängig davon, dass jetzt das GANZE Gespräch gezeigt wird.
  const quelle: "form" | "email" | "notiz" | null = istFormular ? "form" : ersteEingehende ? "email" : notizText ? "notiz" : null;
  const quelleLabel =
    quelle === "form"
      ? "Aus dem Kontaktformular"
      : quelle === "email"
        ? `Erste E-Mail vom ${formatiereDatum(sendezeitpunkt(ersteEingehende!))}`
        : quelle === "notiz"
          ? "Von Hand erfasst"
          : null;

  // Original/Deutsch-Umschalter (unveraendert aus der alten Anfrage-Karte): haengt an
  // customerNotes/inquiryTranslation — nur relevant, wenn die synthetische Gast-Blase ueberhaupt
  // entsteht (quelle !== "email"). Eine E-Mail wird nie uebersetzt.
  const bindetUebersetzung = quelle !== "email";
  const hatUebersetzung = bindetUebersetzung && Boolean(inquiryTranslation?.textDe);
  const zeigtDeutsch = bindetUebersetzung && ansicht === "de" && hatUebersetzung;

  // Leerer Zustand (Vorgabe): keine einzige E-Mail, aber ein Anfragetext (Formular ODER manuell
  // erfasst) -> dieser Text wird zur ERSTEN Gast-Blase, an der frühesten bekannten Zeitstelle
  // (Statuspillen sind DESC sortiert, s. `lib/auftrag-verlauf.ts` — das letzte Element ist damit
  // das früheste).
  const synthetischeGastNachricht: GespraechEmailRow | null = useMemo(() => {
    if (emails.length > 0 || !notizText) return null;
    const frueheste = verlaufEintraege[verlaufEintraege.length - 1]?.zeitpunkt ?? new Date().toISOString();
    return {
      id: SYNTHETISCHE_NACHRICHT_ID,
      direction: "inbound",
      fromAddr: kundenName ?? "Gast",
      toAddr: null,
      subject: null,
      bodyText: zeigtDeutsch ? (inquiryTranslation!.textDe as string) : notizText,
      status: null,
      error: null,
      aiClassification: null,
      receivedAt: frueheste,
      rawHeaders: null,
      attachmentsManifest: null,
    };
  }, [emails.length, notizText, verlaufEintraege, kundenName, zeigtDeutsch, inquiryTranslation]);

  const alleNachrichten = useMemo(() => (synthetischeGastNachricht ? [synthetischeGastNachricht, ...sortierteEmails] : sortierteEmails), [synthetischeGastNachricht, sortierteEmails]);

  // Verlauf-Reiter: alles ausser `mail`/`version` — die erscheinen bereits als Nachricht im Gast- bzw.
  // Betrieb-Reiter, eine zusaetzliche Zeile waere eine Dopplung desselben Ereignisses.
  const ereignisse = useMemo(() => verlaufEintraege.filter((e) => e.art !== "mail" && e.art !== "version"), [verlaufEintraege]);

  async function testUmschalten() {
    if (!inquiryId) return;
    setFehler(null);
    try {
      await apiMutate(`/api/inquiries/${inquiryId}/test`, "PUT", { isTest: !inquiryIsTest });
      onChanged?.();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    }
  }

  async function jetztUebersetzen() {
    if (!inquiryId) return;
    setUebersetzeLaeuft(true);
    setUebersetzeFehler(null);
    try {
      await apiMutate(`/api/inquiries/${inquiryId}/translate`, "POST");
      setAnsicht("de");
      onTranslated?.();
    } catch (err) {
      const code = (err as ApiError)?.code;
      setUebersetzeFehler(
        code === "translator_unavailable"
          ? "KI-Übersetzung ist nicht konfiguriert."
          : code === "no_text"
            ? "Diese Anfrage hat keinen Freitext zum Übersetzen."
            : err instanceof Error
              ? err.message
              : String(err),
      );
    } finally {
      setUebersetzeLaeuft(false);
    }
  }

  // Sprache-Chip/Umschalter nur, wenn ueberhaupt eine uebersetzbare Gast-Blase existiert (nicht
  // bei "email" als Quelle — eine echte Mail wird nicht uebersetzt, unveraendert aus der alten Karte).
  const sprache = bindetUebersetzung ? (inquiryTranslation?.lang && inquiryTranslation.lang !== "de" ? inquiryTranslation.lang : inquiryLanguage) : null;

  return (
    <Card as="section" className="min-w-0 p-6" data-testid="auftrag-gespraech-karte">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className={CARD_TITLE}>Gespräch</h3>
          {quelleLabel && (
            <span data-testid="anfrage-quelle-badge" className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-on-surface-variant dark:bg-white/[0.08]">
              {quelleLabel}
            </span>
          )}
          <HerkunftPille herkunft={herkunft} isTest={inquiryIsTest} />
          {sprache && sprache !== "de" && (
            <span data-testid="anfrage-sprache-chip" className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-on-surface-variant dark:bg-white/[0.08]">
              {LANG_LABEL[sprache] ?? sprache.toUpperCase()}
            </span>
          )}
          {serviceKind && (
            <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-on-surface-variant dark:bg-white/[0.08]">
              {SERVICE_KIND_LABEL[serviceKind] ?? serviceKind}
            </span>
          )}
          <span className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
            {alleNachrichten.length} Nachricht{alleNachrichten.length === 1 ? "" : "en"}
          </span>
        </div>
        {inquiryId && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="test-kennzeichen-umschalten"
              onClick={() => void testUmschalten()}
              className="font-body-sm text-[11.5px] font-semibold text-on-surface-variant hover:text-primary hover:underline"
            >
              {inquiryIsTest ? "Test-Kennzeichen entfernen" : "Als Testanfrage kennzeichnen"}
            </button>
            <Link to={`/anfragen/${inquiryId}`} className="flex items-center gap-1 font-body-sm text-[11.5px] font-semibold text-primary hover:underline">
              Zur Anfrage
              <Icon name="arrow_forward" className="text-[14px]" />
            </Link>
          </div>
        )}
      </div>
      {fehler && <p className="mb-2 font-body-sm text-[11.5px] text-error">{fehler}</p>}

      {hatUebersetzung && (
        <div className="mb-3 inline-flex items-center gap-1 rounded-full border border-outline-variant/60 p-0.5 dark:border-outline/40" role="tablist" aria-label="Original oder Deutsch anzeigen (Gast-Nachricht)">
          {(["original", "de"] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={ansicht === v}
              data-testid={`anfrage-ansicht-${v}`}
              onClick={() => setAnsicht(v)}
              className={`rounded-full px-3 py-1 font-body-sm text-[11.5px] font-semibold transition-colors ${
                ansicht === v ? "bg-primary/[0.1] text-primary" : "text-on-surface-variant hover:text-on-background dark:hover:text-inverse-on-surface"
              }`}
            >
              {v === "original" ? "Original" : "Deutsch"}
            </button>
          ))}
        </div>
      )}

      {bindetUebersetzung && !hatUebersetzung && inquiryId && (
        <div className="mb-3 flex items-center gap-2">
          <Button variant="secondary" className="!py-1.5 !text-[11.5px]" disabled={uebersetzeLaeuft} onClick={() => void jetztUebersetzen()}>
            {uebersetzeLaeuft ? "Übersetzt …" : "Jetzt übersetzen"}
          </Button>
          {uebersetzeFehler && <span className="font-body-sm text-[11px] text-error">{uebersetzeFehler}</span>}
        </div>
      )}

      {deliveryAddress && (
        <p className="mb-3 flex items-start gap-1.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
          <Icon name="local_shipping" className="mt-0.5 shrink-0 text-[14px]" />
          <span>Lieferadresse aus der Anfrage: {deliveryAddress}</span>
        </p>
      )}

      {/* Zuordnung (28.09.2026): dezenter Knopf im Kopf statt der entfallenen Karte „Zugeordnete
          Nachrichten“ — öffnet denselben Auswahl-Drawer wie zuvor. */}
      <div className="mb-3 flex justify-end">
        <Button type="button" variant="secondary" className="!py-1.5 !text-[12.5px]" onClick={() => setZuordnenOffen(true)}>
          <Icon name="mail" className="text-[15px]" /> E-Mail zuordnen
        </Button>
      </div>

      <GespraechMailfenster
        eventId={eventId}
        nachrichten={alleNachrichten}
        ladend={emailsLoading}
        verlaufEintraege={ereignisse}
        kundenName={kundenName}
        kundenFirma={kundenFirma}
        firmaName={firmaName}
        kundenEmail={kundenEmail}
        vorgangInfo={vorgangInfo}
        onGesendet={onGesendet}
        onZuordnungGeaendert={onZuordnungGeaendert}
      />

      <NachrichtAuswaehlenDrawer
        open={zuordnenOffen}
        onClose={() => setZuordnenOffen(false)}
        eventId={eventId}
        onZugeordnet={() => onZuordnungGeaendert?.()}
      />
    </Card>
  );
}
